import {
  isRegisteredPreviewSend,
  type DecisionEngineDestinationDefinition,
  type GenerateSyncConfigParams,
} from "./definition";
import { hightouchMappingWithTags } from "./sync-config-helpers";

function buildBrazeAdditionalConfig({
  isPreview,
  sendToHightouchUser,
  previewRecipientMode,
}: Pick<
  GenerateSyncConfigParams,
  "isPreview" | "sendToHightouchUser" | "previewRecipientMode"
>) {
  // Registered mode targets the customer-registered account profile and must
  // never create or mutate Braze records, so it uses the exact production
  // shape: sendToExistingOnly stays true and no attribute mappings are sent.
  if (isRegisteredPreviewSend({ isPreview, previewRecipientMode })) {
    return {
      userMapping: {
        type: "standard",
        from: "external_id",
        to: "external_user_id",
      },
      sendToExistingOnly: true,
    };
  }
  if (isPreview && sendToHightouchUser) {
    return {
      sendToExistingOnly: false,
      attributeMappings: [
        { type: "standard", from: "external_id", to: "email" },
        {
          type: "standard",
          from: "is_preview",
          to: "is_hightouch_preview_user",
        },
      ],
      userMapping: { type: "standard", from: "id", to: "external_user_id" },
    };
  }
  return {
    userMapping: {
      type: "standard",
      from: "external_id",
      to: "external_user_id",
    },
    ...(isPreview
      ? {
          sendToExistingOnly: false,
          attributeMappings: [
            {
              type: "standard",
              from: "is_preview",
              to: "is_hightouch_preview_user",
            },
          ],
        }
      : { sendToExistingOnly: true }),
  };
}

// Braze is the one AID destination where `mode` is customer-tunable: AID does
// not emit it, and the rETL trigger sync treats it as pure CDC semantics
// (which diff rows fire triggers) rather than a load-bearing literal. Batch
// size is hardcoded to 50 in the destination; parallelization is read from
// config at runtime (default 10) with no rETL form cap, so 100 just keeps
// overrides sane.
const brazeSyncTuning = {
  modes: ["insert", "update", "upsert"],
  parallelization: { max: 100 },
} as const;

export const brazeTriggerDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "braze",
  category: "trigger",
  syncTuning: brazeSyncTuning,
  generateSyncConfig: ({
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    messageTags,
  }) => ({
    type: "trigger",
    campaignId: { type: "standard", from: "campaign_id" },
    mappings: [hightouchMappingWithTags(messageTags)],
    ...buildBrazeAdditionalConfig({
      isPreview,
      sendToHightouchUser,
      previewRecipientMode,
    }),
  }),
};

export const brazeCanvasDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "braze",
  // "trigger" refers to the destination framework category (trigger/event/batch),
  // not the Braze campaign type. Canvas uses the trigger framework.
  category: "trigger",
  syncTuning: brazeSyncTuning,
  generateSyncConfig: ({
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    messageTags,
  }) => ({
    type: "canvas",
    canvasId: { type: "standard", from: "campaign_id" },
    mappings: [hightouchMappingWithTags(messageTags)],
    ...buildBrazeAdditionalConfig({
      isPreview,
      sendToHightouchUser,
      previewRecipientMode,
    }),
  }),
};
